import { useState, useEffect, useRef } from 'react';
import { getI18nStrings } from '../data/i18n';
import { useLanguage } from '../hooks/useLanguage';
import './DrawioViewer.css';
import '../styles/markdown-content.css';

interface DrawioViewerProps {
  filename: string;
}

/**
 * draw.io XMLファイルまたはSVGファイルを表示するコンポーネント
 * .dioファイル: draw.ioのViewerをiframeで埋め込む方式を使用
 * .svgファイル: 直接表示し、Mermaidと同じ拡大機能を提供
 */
function DrawioViewer({ filename }: DrawioViewerProps) {
  const lang = useLanguage();
  const strings = getI18nStrings(lang);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const svgRef = useRef<HTMLImageElement>(null);
  const modalSvgRef = useRef<HTMLDivElement>(null);
  const isSvg = filename.endsWith('.svg');
  
  // モーダル関連のstate
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [scale, setScale] = useState(1.0);
  const [initialScale, setInitialScale] = useState(1.0);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isPinching, setIsPinching] = useState(false);
  const [pinchStartDistance, setPinchStartDistance] = useState(0);
  const [pinchStartScale, setPinchStartScale] = useState(1.0);
  const [showPinchHint, setShowPinchHint] = useState(false);

  useEffect(() => {
    if (!filename) {
      setError(strings.about.architecture.noDiagram);
      setLoading(false);
      return;
    }

    // SVGファイルの場合は、直接表示するだけなので、ローディングを即座に終了
    if (isSvg) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // draw.ioのViewerを使用してXMLファイルを表示
    // public/diagrams/ディレクトリ内のファイルを読み込む
    const diagramUrl = `/diagrams/${filename}`;
    
    let timeoutId: NodeJS.Timeout | null = null;
    let loadHandler: (() => void) | null = null;
    let errorHandler: (() => void) | null = null;
    
    // draw.io ViewerのURLを構築
    // XMLファイルの内容を取得して、draw.io Viewerに渡す
    const loadDiagram = async () => {
      try {
        const response = await fetch(diagramUrl);
        if (!response.ok) {
          throw new Error('Failed to load diagram');
        }
        const xmlContent = await response.text();
        
        // .dioファイルはXML形式なので、Base64エンコードしてdraw.io Viewerに渡す
        // draw.io Viewerは#RパラメータでBase64エンコードされたXMLを受け取ることができます
        const base64Xml = btoa(unescape(encodeURIComponent(xmlContent)));
        
        // URLの長さ制限をチェック
        // ブラウザのURL長制限は通常2000文字以上ですが、実際には多くのブラウザは
        // 8000文字以上のURLもサポートしています。制限を大幅に緩和します。
        // もしエラーが発生する場合は、XMLファイルを圧縮するか、別の方法を検討してください。
        const MAX_URL_LENGTH = 50000; // 50KBに制限を緩和
        if (base64Xml.length > MAX_URL_LENGTH) {
          console.warn(`Diagram XML is too large (${base64Xml.length} chars). Max: ${MAX_URL_LENGTH} chars. Consider compressing the diagram.`);
          setError(strings.about.architecture.noDiagram);
          setLoading(false);
          return;
        }
        
        // draw.io ViewerのURLを構築
        // #RパラメータでBase64エンコードされたXMLを渡す
        const viewerUrl = `https://viewer.diagrams.net/?highlight=0000ff&edit=_blank&layers=1&nav=1&title=System%20Architecture#R${base64Xml}`;
        
        // iframeにURLを設定
        if (iframeRef.current) {
          // ローディング状態を追跡するフラグ
          let isStillLoading = true;
          
          // ロード完了を検知
          loadHandler = () => {
            isStillLoading = false;
            setLoading(false);
          };
          
          // エラーを検知
          errorHandler = () => {
            isStillLoading = false;
            setError(strings.about.architecture.noDiagram);
            setLoading(false);
          };
          
          iframeRef.current.src = viewerUrl;
          iframeRef.current.addEventListener('load', loadHandler);
          iframeRef.current.addEventListener('error', errorHandler);
          
          // タイムアウトを設定（10秒）
          timeoutId = setTimeout(() => {
            if (isStillLoading) {
              isStillLoading = false;
              setError(strings.about.architecture.noDiagram);
              setLoading(false);
            }
          }, 10000);
        }
      } catch (err) {
        console.error('Error loading diagram:', err);
        setError(strings.about.architecture.noDiagram);
        setLoading(false);
      }
    };

    loadDiagram();

    // クリーンアップ
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (iframeRef.current && loadHandler) {
        iframeRef.current.removeEventListener('load', loadHandler);
      }
      if (iframeRef.current && errorHandler) {
        iframeRef.current.removeEventListener('error', errorHandler);
      }
    };
  }, [filename, strings, isSvg]);

  if (error) {
    return (
      <div className="drawio-viewer__error">
        <p>{error}</p>
      </div>
    );
  }

  // SVGファイルの拡大機能
  useEffect(() => {
    if (!isSvg || !isModalOpen) return;

    const calculateInitialScale = () => {
      if (!modalSvgRef.current) return;

      const img = modalSvgRef.current.querySelector('img');
      if (!img) return;

      // 画像の自然なサイズを取得
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;

      if (naturalWidth === 0 || naturalHeight === 0) {
        // 画像がまだ読み込まれていない場合、onLoadイベントを待つ
        return;
      }

      // コンテナのサイズを取得（パディングを考慮）
      const container = modalSvgRef.current.closest('.drawio-modal-content');
      const diagramContainer = modalSvgRef.current.closest('.drawio-modal-diagram');
      const innerDiv = modalSvgRef.current; // .drawio-modal-diagram > div
      
      if (container && diagramContainer && innerDiv) {
        // 図表エリアの実際のサイズを取得
        const diagramWidth = diagramContainer.clientWidth;
        const diagramHeight = diagramContainer.clientHeight;
        
        // 内側のdivのパディングを取得
        const computedStyle = window.getComputedStyle(innerDiv);
        const paddingTop = parseFloat(computedStyle.paddingTop) || 20;
        const paddingLeft = parseFloat(computedStyle.paddingLeft) || 20;
        const paddingRight = parseFloat(computedStyle.paddingRight) || 20;
        const paddingBottom = parseFloat(computedStyle.paddingBottom) || 20;
        
        // 利用可能なサイズ（パディングを除く）
        const availableWidth = diagramWidth - paddingLeft - paddingRight;
        const availableHeight = diagramHeight - paddingTop - paddingBottom;

        // 画面幅いっぱいに表示される状態を100%とする
        // 幅を基準にスケールを計算
        let calculatedScale = availableWidth / naturalWidth;
        
        // スケール適用後の高さを計算
        const scaledHeight = naturalHeight * calculatedScale;
        
        // 高さがコンテナを超える場合は、高さを基準にスケールを再計算
        // 画像全体が見えるようにするため、高さも考慮してスケールを調整
        if (scaledHeight > availableHeight) {
          // 高さを基準にスケールを計算（画像全体が表示されるように）
          const heightBasedScale = availableHeight / naturalHeight;
          // 幅と高さの両方を考慮して、小さい方を採用（画像全体が表示される）
          calculatedScale = Math.min(calculatedScale, heightBasedScale);
        }

        // 初期スケールを設定（この状態が100% = scale 1.0）
        setInitialScale(calculatedScale);
        setScale(1.0);
        
        // 初期位置を0,0に設定（CSSのjustify-content: centerとalign-items: centerで中央配置）
        // transformは位置移動のみに使用し、拡大縮小は画像のサイズで管理
        setPosition({ x: 0, y: 0 });
        
        // 画像のサイズを直接設定して中央配置を確実にする
        // 少し遅延させてから画像のサイズを設定（DOMが完全にレンダリングされるのを待つ）
        setTimeout(() => {
          const img = modalSvgRef.current?.querySelector('img');
          if (img) {
            const finalScaledWidth = naturalWidth * calculatedScale;
            const finalScaledHeight = naturalHeight * calculatedScale;
            img.style.width = `${finalScaledWidth}px`;
            img.style.height = `${finalScaledHeight}px`;
          }
        }, 150);
      }
    };

    // 少し遅延させてから計算（DOMが完全にレンダリングされるのを待つ）
    const timer = setTimeout(() => {
      const img = modalSvgRef.current?.querySelector('img');
      if (img) {
        if (img.complete && img.naturalWidth > 0) {
          calculateInitialScale();
        } else {
          img.addEventListener('load', calculateInitialScale, { once: true });
        }
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isModalOpen, isSvg]);

  // 拡大機能（5段階: 1.25, 1.5, 1.75, 2.0, 2.5倍）
  const handleZoomIn = () => {
    const zoomLevels = [1.0, 1.25, 1.5, 1.75, 2.0, 2.5];
    const currentIndex = zoomLevels.findIndex(level => Math.abs(level - scale) < 0.01);
    if (currentIndex < zoomLevels.length - 1) {
      setScale(zoomLevels[currentIndex + 1]);
    }
  };

  // 縮小機能
  const handleZoomOut = () => {
    const zoomLevels = [1.0, 1.25, 1.5, 1.75, 2.0, 2.5];
    const currentIndex = zoomLevels.findIndex(level => Math.abs(level - scale) < 0.01);
    if (currentIndex > 0) {
      setScale(zoomLevels[currentIndex - 1]);
    }
  };

  // リセット機能
  const handleZoomReset = () => {
    setScale(1.0);
    setPosition({ x: 0, y: 0 });
  };

  // ドラッグ機能
  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return; // 左クリックのみ
    e.preventDefault(); // デフォルトの動作を防ぐ
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault(); // デフォルトの動作を防ぐ
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault(); // デフォルトの動作を防ぐ
    setIsDragging(false);
  };

  // グローバルマウスイベント
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMouseMove = (e: MouseEvent) => {
        e.preventDefault(); // デフォルトの動作を防ぐ
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      };

      const handleGlobalMouseUp = (e: MouseEvent) => {
        e.preventDefault(); // デフォルトの動作を防ぐ
        setIsDragging(false);
      };

      // passive: falseでpreventDefaultを有効化
      document.addEventListener('mousemove', handleGlobalMouseMove, { passive: false });
      document.addEventListener('mouseup', handleGlobalMouseUp, { passive: false });

      return () => {
        document.removeEventListener('mousemove', handleGlobalMouseMove);
        document.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [isDragging, dragStart, position]);

  // 2点間の距離を計算
  const getDistance = (touch1: Touch, touch2: Touch): number => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // ピンチジェスチャーの開始
  const handleTouchStart = (e: TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      setIsPinching(true);
      setIsDragging(false);
      const distance = getDistance(e.touches[0], e.touches[1]);
      setPinchStartDistance(distance);
      setPinchStartScale(scale);
    } else if (e.touches.length === 1) {
      // 1本指の場合はドラッグとして処理
      setIsDragging(true);
      setDragStart({
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y,
      });
    }
  };

  // ピンチジェスチャーの更新
  const handleTouchMove = (e: TouchEvent) => {
    if (e.touches.length === 2 && isPinching) {
      e.preventDefault();
      const distance = getDistance(e.touches[0], e.touches[1]);
      const scaleRatio = distance / pinchStartDistance;
      const newScale = Math.max(1.0, Math.min(2.5, pinchStartScale * scaleRatio));
      
      // ズームレベルに最も近い値に丸める
      const zoomLevels = [1.0, 1.25, 1.5, 1.75, 2.0, 2.5];
      const closestLevel = zoomLevels.reduce((prev, curr) => 
        Math.abs(curr - newScale) < Math.abs(prev - newScale) ? curr : prev
      );
      setScale(closestLevel);
    } else if (e.touches.length === 1 && isDragging) {
      e.preventDefault();
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y,
      });
    }
  };

  // ピンチジェスチャーの終了
  const handleTouchEnd = (e: TouchEvent) => {
    if (e.touches.length < 2) {
      setIsPinching(false);
    }
    if (e.touches.length === 0) {
      setIsDragging(false);
    }
  };

  // スケールに応じて画像のサイズを更新
  useEffect(() => {
    if (isModalOpen && modalSvgRef.current && initialScale > 0 && isSvg) {
      const img = modalSvgRef.current.querySelector('img');
      if (img && img.naturalWidth > 0 && img.naturalHeight > 0) {
        const newWidth = img.naturalWidth * initialScale * scale;
        const newHeight = img.naturalHeight * initialScale * scale;
        img.style.width = `${newWidth}px`;
        img.style.height = `${newHeight}px`;
      }
    }
  }, [scale, initialScale, isModalOpen, isSvg]);

  // タッチイベントリスナーの登録
  useEffect(() => {
    if (!isModalOpen || !isSvg) return;

    const diagramElement = modalSvgRef.current?.parentElement;
    if (!diagramElement) return;

    diagramElement.addEventListener('touchstart', handleTouchStart, { passive: false });
    diagramElement.addEventListener('touchmove', handleTouchMove, { passive: false });
    diagramElement.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      diagramElement.removeEventListener('touchstart', handleTouchStart);
      diagramElement.removeEventListener('touchmove', handleTouchMove);
      diagramElement.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isModalOpen, isSvg, isPinching, isDragging, pinchStartDistance, pinchStartScale, scale, dragStart, position]);

  const handleClick = () => {
    setIsModalOpen(true);
    setScale(1.0);
    // 位置はcalculateInitialScaleで計算されるため、ここでは設定しない
    // スマホではピンチイン・アウトのヒントを表示
    if (window.innerWidth <= 768) {
      setShowPinchHint(true);
      setTimeout(() => {
        setShowPinchHint(false);
      }, 3000);
    }
  };

  const handleCloseModal = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsModalOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsModalOpen(false);
    }
  };

  // SVGファイルの場合は拡大機能付きで表示
  if (isSvg) {
    const diagramUrl = `/diagrams/${filename}`;
    return (
      <>
        <div className="drawio-viewer drawio-viewer--clickable" onClick={handleClick} title="クリックで拡大表示">
          <img
            ref={svgRef}
            src={diagramUrl}
            alt="System Architecture Diagram"
            className="drawio-viewer__svg"
            onLoad={() => setLoading(false)}
            onError={() => {
              setError(strings.about.architecture.noDiagram);
              setLoading(false);
            }}
          />
          <div className="drawio-viewer__overlay">
            <span className="drawio-viewer__expand-icon">🔍</span>
          </div>
          {loading && (
            <div className="drawio-viewer__loading">
              <p>{strings.about.architecture.loadingDiagram}</p>
            </div>
          )}
          {error && (
            <div className="drawio-viewer__error">
              <p>{error}</p>
            </div>
          )}
        </div>
        {isModalOpen && (
          <div
            className="drawio-modal"
            onClick={handleCloseModal}
            onKeyDown={handleKeyDown}
            tabIndex={-1}
          >
            <div className="drawio-modal-content" onClick={(e) => e.stopPropagation()}>
              <button
                className="drawio-modal-close"
                onClick={() => setIsModalOpen(false)}
                aria-label="閉じる"
              >
                ×
              </button>
              <div className="drawio-modal-controls drawio-modal-controls--desktop">
                <button
                  className="drawio-control-btn"
                  onClick={handleZoomIn}
                  disabled={scale >= 2.5}
                  title="拡大 (+)"
                  aria-label="拡大"
                >
                  +
                </button>
                <button
                  className="drawio-control-btn"
                  onClick={handleZoomOut}
                  disabled={scale <= 1.0}
                  title="縮小 (-)"
                  aria-label="縮小"
                >
                  −
                </button>
                <button
                  className="drawio-control-btn"
                  onClick={handleZoomReset}
                  disabled={scale === 1.0}
                  title="リセット"
                  aria-label="リセット"
                >
                  ↻
                </button>
                <span className="drawio-zoom-info">{Math.round(scale * 100)}%</span>
              </div>
              {showPinchHint && (
                <div className="drawio-pinch-hint">
                  <div className="drawio-pinch-hint-icon">🤏</div>
                  <div className="drawio-pinch-hint-text">ピンチイン・アウトで拡大・縮小できます</div>
                </div>
              )}
              <div
                className="drawio-modal-diagram"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                style={{ 
                  cursor: isDragging ? 'grabbing' : 'grab', 
                  touchAction: 'none',
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                }}
              >
                <div
                  ref={modalSvgRef}
                  style={{
                    transform: `translate(${position.x}px, ${position.y}px)`,
                    transition: (isDragging || isPinching) ? 'none' : 'transform 0.2s ease-out',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    willChange: isDragging ? 'transform' : 'auto',
                  }}
                >
                  <img
                    src={diagramUrl}
                    alt="System Architecture Diagram"
                    style={{
                      display: 'block',
                      maxWidth: 'none',
                      maxHeight: 'none',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  // .dioファイルの場合はiframeで表示
  return (
    <div className="drawio-viewer">
      {loading && (
        <div className="drawio-viewer__loading">
          <p>{strings.about.architecture.loadingDiagram}</p>
        </div>
      )}
      {/* 
        sandbox属性の警告について:
        draw.io Viewerを使用するため、allow-scriptsとallow-same-originの両方が必要です。
        これによりセキュリティ警告が表示されますが、draw.io Viewerは信頼できるソース（https://viewer.diagrams.net）です。
        本番環境では問題ありません。
      */}
      <iframe
        ref={iframeRef}
        className="drawio-viewer__iframe"
        title="System Architecture Diagram"
        style={{ display: loading ? 'none' : 'block' }}
        sandbox="allow-scripts allow-same-origin allow-popups"
      />
    </div>
  );
}

export default DrawioViewer;
